import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  trackOnboardingError,
  trackOnboardingEvent,
} from "../analytics/onboarding";
import { trackFinancialError, trackFinancialEvent } from "../analytics/financial";
import { ROUTE } from "../constants/routes";
import type { FormChatState } from "../repositories/formChat.repository";
import { buildMonthlyFeeSavePayload } from "../utils/monthlyFeeFormChatPayloadBuilder";
import { toast } from "../utils/toast";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";
import { useUpdateMonthlyFeeMutation } from "./useUpdateMonthlyFeeMutation";

const SOURCE_PAGE = "FormChatAssistant";

export class MonthlyFeeChatSubmitError extends Error {}

export function useFormChatSubmitMonthlyFee({
  monthlyFeeId,
}: {
  monthlyFeeId?: string;
}) {
  const navigate = useNavigate();
  const productBase = useProductAnalyticsBase();
  const terreiroId = useCachedTerreiro()?.id;
  const updateMutation = useUpdateMonthlyFeeMutation();

  const navigateBack = useCallback(() => {
    navigate(ROUTE.FINANCIAL_MONTHLY_FEE);
  }, [navigate]);

  const submitMonthlyFeeByChat = useCallback(
    async (state: FormChatState) => {
      const payload = monthlyFeeId
        ? buildMonthlyFeeSavePayload(state, monthlyFeeId)
        : null;
      if (!payload || !monthlyFeeId || !terreiroId) {
        throw new MonthlyFeeChatSubmitError(
          "Revise os dados da mensalidade no chat antes de confirmar.",
        );
      }
      const analyticsBase = { ...productBase, trigger: "mutation" as const };
      const onboardingBase = {
        sourcePage: SOURCE_PAGE,
        terreiroId,
        trigger: "mutation" as const,
      };
      const eventProps = { monthly_fee_id: monthlyFeeId, source: "form_chat" };

      trackFinancialEvent(
        "financial_monthly_fee_update_submit_attempt",
        analyticsBase,
        eventProps,
      );
      try {
        await updateMutation.mutateAsync({ terreiroId, monthlyFeeId, payload });
      } catch (error) {
        trackOnboardingError("onboarding_monthly_fee_edited", onboardingBase, error, {
          operation: "edit",
          ...eventProps,
        });
        trackFinancialError(
          "financial_monthly_fee_update_error",
          analyticsBase,
          error,
          eventProps,
        );
        throw error;
      }

      trackOnboardingEvent("onboarding_monthly_fee_edited", onboardingBase, {
        operation: "edit",
        ...eventProps,
      });
      trackFinancialEvent("financial_monthly_fee_updated", analyticsBase, eventProps);
      toast.success("Mensalidade atualizada.");
      navigateBack();
    },
    [monthlyFeeId, navigateBack, productBase, terreiroId, updateMutation],
  );

  return {
    submitMonthlyFeeByChat,
    navigateBack,
    isSubmitting: updateMutation.isPending,
  };
}
